<script setup>
import {
  computed,
  ref,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
} from 'vue';
import EmailThreadMessage from './EmailThreadMessage.vue';
import { emitter } from 'shared/helpers/mitt';

// Card de thread de email — SEMPRE expandido (sem accordion).
// Antes era sanfona, mas a UX ficou ruim (gente nao entendia que tinha
// que clicar pra abrir, e quando abria perdia a referencia das outras).
// Decisao 06/06: cards inline na timeline, todos expandidos. Cada card
// tem header com assunto + contador + ultima atividade, lista todas as
// mensagens da thread em ordem cronologica e um botao "Responder esta
// thread" no rodape.

const props = defineProps({
  threadId: { type: String, required: true },
  messages: { type: Array, required: true },
  subject: { type: String, default: '' },
  isLast: { type: Boolean, default: false },
  showReplyButton: { type: Boolean, default: true },
});

const emit = defineEmits(['replyThread']);

const cardRef = ref(null);

const totalCount = computed(() => props.messages.length);

const lastActivity = computed(() => {
  const dates = props.messages
    .map(m => m.createdAt || m.created_at || 0)
    .filter(Boolean);
  return dates.length ? Math.max(...dates) : 0;
});

const sortedMessages = computed(() =>
  [...props.messages].sort(
    (a, b) =>
      (a.createdAt || a.created_at || 0) - (b.createdAt || b.created_at || 0)
  )
);

const formatTime = ts => {
  if (!ts) return '';
  const d = new Date(ts * 1000);
  const now = new Date();
  const sameDay =
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear();
  if (sameDay) {
    return d.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

const replyToThread = () => {
  emit('replyThread', { threadId: props.threadId, subject: props.subject });
};

const displaySubject = computed(() => {
  const s = (props.subject || '').replace(/^\s*(re|fwd?)\s*:\s*/i, '').trim();
  return s || '(sem assunto)';
});

// Quando "+Novo email" cria a thread que esse card representa, dispara
// scroll suave pra essa thread aparecer no centro da viewport. Tambem
// destaca por uns segundos via `recently-opened`.
const recentlyOpened = ref(false);
const onThreadOpened = ({ threadId }) => {
  if (threadId !== props.threadId) return;
  recentlyOpened.value = true;
  nextTick(() =>
    cardRef.value?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  );
  setTimeout(() => {
    recentlyOpened.value = false;
  }, 3000);
};
onMounted(() => emitter.on('email-thread:opened', onThreadOpened));
onBeforeUnmount(() => emitter.off('email-thread:opened', onThreadOpened));

// Auto-scroll pra ULTIMA thread quando ela renderiza pela primeira vez
// (assim o agente abre a conversa e ja ve o assunto mais recente).
watch(
  () => props.isLast,
  v => {
    if (v)
      nextTick(() =>
        cardRef.value?.scrollIntoView({ behavior: 'auto', block: 'end' })
      );
  },
  { immediate: true }
);

const labels = {
  msgSingular: 'email',
  msgPlural: 'emails',
  lastSep: '·',
  lastPrefix: 'última',
  replyThread: 'Responder esta thread',
  threadIcon: '🧵',
};
</script>

<template>
  <li
    ref="cardRef"
    class="email-thread-card block flex-none shrink-0 overflow-visible my-4 border border-n-weak rounded-xl bg-n-background transition-colors"
    :class="{ 'ring-2 ring-n-brand': recentlyOpened, 'border-n-brand': isLast }"
  >
    <header
      class="flex items-center gap-2 px-4 py-3 bg-n-slate-2 border-b border-n-weak"
    >
      <span class="text-base">{{ labels.threadIcon }}</span>
      <div class="flex-1 min-w-0">
        <div class="text-sm font-semibold text-n-slate-12 truncate">
          {{ displaySubject }}
        </div>
        <div class="text-xs text-n-slate-10 mt-0.5">
          {{ totalCount }}
          {{ totalCount === 1 ? labels.msgSingular : labels.msgPlural }}
          <span class="mx-1">{{ labels.lastSep }}</span>
          {{ labels.lastPrefix }}: {{ formatTime(lastActivity) }}
        </div>
      </div>
    </header>

    <div class="px-3 py-2">
      <EmailThreadMessage
        v-for="msg in sortedMessages"
        :key="msg.id"
        :message="msg"
      />
    </div>

    <footer
      v-if="showReplyButton"
      class="px-4 py-2 border-t border-n-weak bg-n-slate-2 text-right"
    >
      <button
        type="button"
        class="text-xs text-n-brand hover:underline font-medium inline-flex items-center gap-1"
        @click="replyToThread"
      >
        <span class="i-lucide-corner-up-left size-3" />
        {{ labels.replyThread }}
      </button>
    </footer>
  </li>
</template>
